import { P2GAlert, P2GBox, P2GRadio, P2GRadioGroup } from '@parcel2go/core-ui';
import { P2GButton } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';
import { useCallback, useMemo } from 'react';
import { useState } from 'react';
import { Country, InputRuleApiModel, PackageApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { SumNumber } from '../../../lib/helpers/ArrayHelper';
import { SmartSendItemGroup } from '../item/SmartSendGroup';
import SidePanelSection, { SidePanelSections } from '../../../../../components/sidepanel-section';
import { FilterToSendableItems } from '../panels/FilterToSendableItems';
import { GenerateCombiningSummary } from './GroupingHelper';
import { ConsignmentBuilder, ShipmentDimensionsSection } from './ConsignmentBuilder';
import { GenerateConsignmentFromItem } from '../panels/consignments/GenerateConsignmentFromItem';
import { consignmentParcelBound } from '~/apps/smartsend/lib/parcels/bounds';
import { distributeIntoParcels, linesToContents } from '~/apps/smartsend/lib/parcels/allocate';
import { contentIsIncomplete } from '~/apps/smartsend/lib/parcels/rules';
import { useParcelPlan } from '~/apps/smartsend/lib/parcels/useParcelPlan';
import { Custom } from './Custom';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import ConsignmentSummaryParcels from '../panels/consignments/ConsignmentSummaryParcels';
import StickyButtons from '~/components/sticky';

type GroupType = 'MERGE' | 'MULTI-PARCEL-CONSIGNMENT' | 'SPLIT-DIFFERENT-SERVICES';

export interface props {
  group: SmartSendItemGroup;
  getPackages: () => Promise<PackageApiModel[]>;
  create: (items: SmartSendItemApiModel[]) => void;
  countries?: Country[];
  productValidation?: InputRuleApiModel[];
  tariffSystem?: string;
  allowSplitToDifferentServices?: boolean;
}

export const Combining: React.FC<props> = (props) => {
  // The merged shipment starts with the combined weight of everything going into it.
  const [initialDims] = useState(() => ({
    Height: 0,
    Width: 0,
    Length: 0,
    Weight: SumNumber(props.group.Items.map((x) => x.PackageDimensions.Weight || 0))
  }));

  const [groupType, setGroupType] = useState<GroupType>('MERGE');
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const sendableLines = useCallback(
    () => props.group.Items.flatMap((i) => i.MarketplaceOrderLines.filter(FilterToSendableItems)),
    [props.group.Items]
  );

  // A multi-parcel consignment is one shipment in several boxes, so the count is a packaging choice
  // rather than a property of the order - but only a SINGLE order line can be padded out beyond its
  // unit count. Combining a group almost always has several lines, so this usually clamps, and offering
  // more than the allocator will build put dead options on screen. See consignmentParcelBound.
  const maxParcels = useMemo(() => consignmentParcelBound(sendableLines()), [sendableLines]);

  const perOrderCount = Math.min(props.group.Items.length, maxParcels);

  // One shipment either way, so one plan either way: a single parcel holding everything for MERGE,
  // or the allocator's distribution for the multi-parcel case.
  const parcels = useParcelPlan({
    initialParcelCount: 1,
    initialDimensions: initialDims,
    seed: (count, dims) => {
      const lines = sendableLines();
      const pseudoItem: SmartSendItemApiModel = { ...props.group.Items[0], MarketplaceOrderLines: lines };

      return count <= 1
        ? distributeIntoParcels(linesToContents(lines), 1, {
            granularity: 'rows',
            overflow: 'clamp',
            parcelId: () => null,
            dimensions: dims,
            canAdd: true,
            splitWeight: true
          })
        : GenerateConsignmentFromItem(pseudoItem, dims, count);
    }
  });

  const { plan, parcelCount, dimensions, setParcelCount, setDimensions } = parcels;

  // The merged shipment always inherits Items[0]'s service (GenerateCombiningSummary uses
  // Items[0] as its base), so what matters is whether THAT item's service supports it - not
  // whether any item in the group happens to.
  const siteAndItemSupportsConsignments = SettingsCache.settings.Features.includes('Consignments');

  // Gated on what the allocator will actually build rather than on a unit count. A single order line
  // pads across parcels, so one qty-1 line still yields a range - which is what the item panel already
  // offers, and gating on units here made the two surfaces disagree.
  const consignmentOptionEnabled = siteAndItemSupportsConsignments && maxParcels > 1;
  const showParcelOptions = consignmentOptionEnabled && groupType === 'MULTI-PARCEL-CONSIGNMENT';

  // The plan is authoritative for how many parcels there actually are - the allocator may have
  // clamped below what was asked for.
  const displayedParcelCount = plan.Parcels.length;

  // MERGE renders its single parcel read-only, so only the multi-parcel path can be edited into an
  // incomplete state. No fallback needed: the plan is always current.
  const hasInvalidConsignmentContent = groupType === 'MULTI-PARCEL-CONSIGNMENT' && contentIsIncomplete(plan);

  function combine() {
    const summary = GenerateCombiningSummary(props.group, dimensions);
    if (summary) {
      if (parcelCount > 1) {
        // No clone and no weight re-split here: setDimensions already reflowed per-parcel weight
        // onto the live plan, so it is never stale against the current dimensions.
        summary.newItem.Consignment = plan;
      }
      props.create(summary.items);
    }
  }

  const currencyCode = props.group.Items[0]?.CurrencyCode;

  const contentContext = useMemo(
    () => ({
      countries: props.countries,
      productValidation: props.productValidation,
      tariffSystem: props.tariffSystem,
      sender: props.group.Items[0]?.Sender,
      recipient: props.group.Items[0]?.Recipient,
      currencyCode
    }),
    [props.countries, props.productValidation, props.tariffSystem, props.group.Items, currencyCode]
  );

  const options = [
    ...(props.group.Items.length > 1
      ? [
          <P2GRadio
            name="GroupType"
            fullWidth
            value="MERGE"
            checked={groupType === 'MERGE'}
            id="merge"
            onChange={() => {
              setParcelCount(1);
              setGroupType('MERGE');
            }}
            label={t('COMBINE_AS_ONE_BOX_RADIO', { defaultValue: 'Send everything in one box' })}
          />
        ]
      : []),
    ...(consignmentOptionEnabled
      ? [
          <P2GRadio
            name="GroupType"
            fullWidth
            value="MULTI-PARCEL-CONSIGNMENT"
            checked={groupType === 'MULTI-PARCEL-CONSIGNMENT'}
            id="multi-parcel-consignment"
            onChange={() => {
              if (parcelCount === 1) {
                setParcelCount(Math.max(perOrderCount, 2));
              }
              setGroupType('MULTI-PARCEL-CONSIGNMENT');
            }}
            label={t('COMBINE_AS_MULTI_PARCEL_RADIO', {
              defaultValue: 'Multiple boxes with one collection/drop-off and one tracking number'
            })}
          />
        ]
      : []),
    ...(props.allowSplitToDifferentServices
      ? [
          <P2GRadio
            name="GroupType"
            fullWidth
            value="SPLIT-DIFFERENT-SERVICES"
            checked={groupType === 'SPLIT-DIFFERENT-SERVICES'}
            id="split-different-services"
            onChange={() => setGroupType('SPLIT-DIFFERENT-SERVICES')}
            label={t('USE_MULTIPLE_SHIPMENTS_BUTTON', {
              defaultValue: 'Multiple boxes with different collection/drop-off and tracking numbers'
            })}
          />
        ]
      : [])
  ];

  return (
    <>
      <SidePanelSections>
        <P2GBox sx={{ '& .MuiFormControlLabel-root': { margin: 0 } }}>
          <P2GRadioGroup name="GroupType" layout="column" children={options} />
        </P2GBox>
        {groupType === 'SPLIT-DIFFERENT-SERVICES' ? (
          <Custom
            group={props.group}
            create={props.create}
            countries={props.countries}
            productValidation={props.productValidation}
            tariffSystem={props.tariffSystem}
            getPackages={props.getPackages}
          />
        ) : (
          <>
            {groupType === 'MULTI-PARCEL-CONSIGNMENT' && (
              <P2GText>
                {t('COMBINE_MULTI_PARCEL_INTRO_TEXT', {
                  defaultValue: `This combines all items into one booking with the courier. You'll receive one reference number and can use multiple boxes to send your items in.`
                })}
              </P2GText>
            )}
            {showParcelOptions ? (
              <ConsignmentBuilder
                parcels={parcels}
                maxParcels={maxParcels}
                content={contentContext}
                getPackages={props.getPackages}
                dimensionsFallback={props.group.Items[0].PackageDimensions}
                onCollapseToSingle={() => setGroupType('MERGE')}
              />
            ) : (
              <>
                <SidePanelSection title={t('SHIPMENT_SUMMARY_SUBTITLE', { defaultValue: 'Shipment Summary' })}>
                  <P2GBox py={2}>
                    {/* MERGE is one box holding everything - a read-only summary, nothing to arrange. */}
                    <ConsignmentSummaryParcels consignment={plan} currencyCode={currencyCode} />
                  </P2GBox>
                </SidePanelSection>
                <ShipmentDimensionsSection
                  dimensions={dimensions}
                  parcelCount={displayedParcelCount}
                  fallback={props.group.Items[0].PackageDimensions}
                  onUpdate={setDimensions}
                  getPackages={props.getPackages}
                />
              </>
            )}
          </>
        )}
      </SidePanelSections>
      {groupType !== 'SPLIT-DIFFERENT-SERVICES' && (
        <>
          <P2GBox mt={2}>
            <P2GAlert type="success">
              <P2GText variant="body2">
                {groupType === 'MULTI-PARCEL-CONSIGNMENT'
                  ? t('COMBINE_MULTI_PARCEL_SUMMARY_TEXT', {
                      defaultValue:
                        'This will combine {{orderCount}} orders into 1 shipment across {{parcelCount}} parcels.',
                      orderCount: props.group.Items.length,
                      parcelCount: displayedParcelCount
                    })
                  : t('COMBINE_MERGE_SUMMARY_TEXT', {
                      defaultValue: 'This will combine {{orderCount}} orders into 1 shipment.',
                      orderCount: props.group.Items.length
                    })}
              </P2GText>
            </P2GAlert>
          </P2GBox>
          <StickyButtons>
            <P2GBox mt={2}>
              <P2GButton variant="primary" disabled={hasInvalidConsignmentContent} onClick={combine}>
                {t('CONFIRM_BUTTON', { defaultValue: 'Confirm' })}
              </P2GButton>
            </P2GBox>
          </StickyButtons>
        </>
      )}
    </>
  );
};
